// A conversation's post box drawn by the host (contract.ts `composer`, the
// "composer" kind): the web keeps what the box holds and does (the draft, the
// files going up, sending), the host draws the box with its own parts and
// hands back what the person does in it.
//
// Promise:
// - Only when the host says it draws the "composer" kind; otherwise (an older
//   app, a plain browser) the web's own box stays.
// - One box at a time: the last one mounted is the host's; when it goes the
//   one before it comes back.
// - The host's text stands while the web's text is the one it last heard
//   from the host. When the web sets another (a draft read back, sent, an
//   image's label put in), `revision` goes up and the host takes it.
// - Files the host hands over are attached as if picked in the web's box.
// - "Your turn" goes inside the box as data (`turn`) for a host that says
//   it draws "composer-turn"; the screen may name the box (`id`: an item
//   opened in it), and another id is another box to the host (its text is
//   swapped). What the host does with the item comes back to `onTurn`.
// - The room the host's box takes at the bottom (`room`) is told to the
//   screen, which keeps the conversation's end above it. While the host's
//   keyboard is up, `html[data-host-keyboard]` says so (the phone's frame
//   then lays nothing out for a keyboard: the host's box sits on it).
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { MeAppContext } from "../../app/context";
import {
  SHELL_CONTRACT_VERSION,
  type ShellComposer,
  type ShellComposerFile,
  type ShellComposerPill,
  type ShellComposerTurn,
  type ShellComposerTurnEvent,
  type ShellComposerUpload,
  type ShellMessageFromHost,
  type ShellSuggest,
} from "./contract";
import { HostAsideContext } from "./floats";
import type { ShellHost } from "./outside";

/** A pill as the box's conditions hand it: its data and what pressing does. */
export interface ShellComposerPillInput extends ShellComposerPill {
  onPress(): void;
}

/**
 * Inside a post box the host draws: the conditions drawn there (an Agent
 * chat's pills) hand their data here instead of drawing their buttons.
 */
export const HostedPillsContext = createContext<
  ((pills: ShellComposerPillInput[] | null) => void) | null
>(null);

/** A post box as the screen hands it to the host: its data and its handlers. */
export interface ShellComposerInput {
  /**
   * The box's id, when the screen names it (an item of "your turn" opened
   * in it: the item's). Absent: one of its own, the same while mounted.
   */
  id?: string;
  placeholder: string;
  text: string;
  canSend: boolean;
  sendLabel: string;
  attach?: string;
  files: ShellComposerFile[];
  pills?: ShellComposerPillInput[];
  /** Where the caret goes, when the web set the text (after a candidate). */
  caret?: number;
  /** The candidates open now. */
  suggest?: ShellSuggest;
  /** "Your turn" inside the box (a host that draws "composer-turn"). */
  turn?: ShellComposerTurn;
  /** What the person did with it there. */
  onTurn?(event: ShellComposerTurnEvent): void;
  onText(text: string): void;
  /** Where the host's caret is (for candidates). */
  onCaret?(caret: number): void;
  /** A candidate chosen in the host's list. */
  onPick?(key: string): void;
  onSend(): void;
  onAttach?(files: File[]): void;
  onRemove?(file: string): void;
}

type Box = { id: string; input: { current: ShellComposerInput | null } };

export interface ComposerStore {
  set(box: Box | null, owner: object): void;
  receive(message: Extract<ShellMessageFromHost, { type: "composer" }>): void;
  /** The room the host's box takes at the bottom (px), by box id. */
  room(id: string): number | null;
  /** Hears when a box's room changes. Returns the unsubscribe. */
  listen(listener: () => void): () => void;
}

export const ComposerContext = createContext<ComposerStore | null>(null);

/** On `<html>` while the host's keyboard is up (its post box is written in). */
export const HOST_KEYBOARD = "data-host-keyboard";
/** Dispatched on the window when that changes. */
export const HOST_KEYBOARD_EVENT = "trameria-host-keyboard";
/**
 * On `<html>` once the host measures the keyboard over its window (the
 * `keyboard` message): its height in px (phone/keyboard.ts keeps to it).
 */
export const HOST_KEYBOARD_HEIGHT = "data-host-keyboard-height";

/** Keeps the host's measure of the keyboard and tells the phone's frame. */
export function setHostKeyboard(height: number) {
  const html = document.documentElement;
  const value = String(Math.round(height));
  if (html.getAttribute(HOST_KEYBOARD_HEIGHT) === value) return;
  html.setAttribute(HOST_KEYBOARD_HEIGHT, value);
  window.dispatchEvent(new Event(HOST_KEYBOARD_EVENT));
}

/** The files handed over, as files the web can attach. */
function filesOf(uploads: readonly ShellComposerUpload[]): File[] {
  return uploads.flatMap((u) => {
    try {
      const bytes = Uint8Array.from(atob(u.data), (c) => c.charCodeAt(0));
      return [new File([bytes], u.name, { type: u.mime })];
    } catch {
      return [];
    }
  });
}

export function createComposerStore(host: ShellHost): ComposerStore {
  let boxes: { owner: object; box: Box }[] = [];
  let sent: string | undefined;
  let revision = 0;
  /** The text the host last sent, per box: the web echoing it is no new text. */
  const heard = new Map<string, string>();
  /** The text last handed to the host, per box. */
  const handed = new Map<string, string>();
  const rooms = new Map<string, number>();
  const listeners = new Set<() => void>();
  const current = () => boxes.at(-1)?.box ?? null;
  const send = () => {
    const box = current();
    const input = box?.input.current;
    let composer: ShellComposer | null = null;
    if (box && input) {
      const fromHost = heard.get(box.id) === input.text;
      if (!fromHost && handed.get(box.id) !== input.text) revision++;
      handed.set(box.id, input.text);
      composer = {
        id: box.id,
        placeholder: input.placeholder,
        text: input.text,
        revision,
        canSend: input.canSend,
        sendLabel: input.sendLabel,
        ...(input.attach ? { attach: input.attach } : {}),
        files: input.files,
        ...(input.pills?.length
          ? { pills: input.pills.map(({ onPress: _, ...p }) => p) }
          : {}),
        ...(input.caret !== undefined ? { caret: input.caret } : {}),
        ...(input.suggest ? { suggest: input.suggest } : {}),
        ...(input.turn ? { turn: input.turn } : {}),
      };
    }
    const json = JSON.stringify(composer);
    if (json === sent) return;
    sent = json;
    host.post({ version: SHELL_CONTRACT_VERSION, type: "composer", composer });
  };
  return {
    set(box, owner) {
      const at = boxes.findIndex((b) => b.owner === owner);
      if (box && at >= 0) boxes[at] = { owner, box };
      else if (box) boxes.push({ owner, box });
      else if (at >= 0) boxes = boxes.filter((b) => b.owner !== owner);
      send();
    },
    receive(message) {
      const box = boxes.find((b) => b.box.id === message.id)?.box;
      const input = box?.input.current;
      // The box may have been renamed since it was handed (another item
      // opened in it): what was done in the box of the old name is dropped.
      if (!box || !input || (input.id ?? box.id) !== message.id) return;
      switch (message.event) {
        case "text":
          heard.set(message.id, message.text);
          input.onText(message.text);
          if (message.caret !== undefined) input.onCaret?.(message.caret);
          return;
        case "pick":
          input.onPick?.(message.key);
          return;
        case "send":
          if (input.canSend) input.onSend();
          return;
        case "attach":
          input.onAttach?.(filesOf(message.files));
          return;
        case "remove":
          input.onRemove?.(message.file);
          return;
        case "pill":
          input.pills?.find((p) => p.id === message.pill)?.onPress();
          return;
        case "turn": {
          const { type: _t, id: _i, event: _e, item, ...turn } = message;
          // Done with an item no longer shown in the box: dropped, so it
          // never lands on the one shown now.
          if (item !== input.turn?.item) return;
          input.onTurn?.(turn as ShellComposerTurnEvent);
          return;
        }
        case "room": {
          const html = document.documentElement;
          if (
            html.hasAttribute(HOST_KEYBOARD) !==
            (message.keyboard === true)
          ) {
            html.toggleAttribute(HOST_KEYBOARD, message.keyboard === true);
            window.dispatchEvent(new Event(HOST_KEYBOARD_EVENT));
          }
          if (rooms.get(message.id) === message.height) return;
          rooms.set(message.id, message.height);
          for (const l of listeners) l();
        }
      }
    },
    room: (id) => rooms.get(id) ?? null,
    listen(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/** Whether the host draws a conversation's post box. */
export function useHostDrawsComposer(): boolean {
  // Also outside the Me app (a box elsewhere): then no host.
  const host = useContext(MeAppContext)?.extensions.shellHost;
  const aside = useContext(HostAsideContext);
  return !aside && (host?.floats?.has("composer") ?? false);
}

/**
 * Hands the post box to the host while mounted (null: not now), when the host
 * draws post boxes. Returns whether it does (then the web draws no box of its
 * own) and the room its box takes at the bottom (null until the host says).
 */
export function useShellComposer(input: ShellComposerInput | null): {
  drawn: boolean;
  room: number | null;
} {
  const draws = useHostDrawsComposer();
  const store = useContext(ComposerContext);
  const own = useId();
  const id = input?.id ?? own;
  const [owner] = useState(() => ({}));
  const latest = useRef(input);
  latest.current = input;
  const on = draws && store !== null && input !== null;
  const key = input
    ? JSON.stringify({
        ...input,
        onText: undefined,
        onSend: undefined,
        onAttach: undefined,
        onRemove: undefined,
        onCaret: undefined,
        onPick: undefined,
        onTurn: undefined,
        pills: input.pills?.map(({ onPress: _, ...p }) => p),
      })
    : null;
  // Tells the host (an outside system) when the box's data changes; the
  // handlers are read from `latest` when it sends something back.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` stands for the data
  useEffect(() => {
    if (!store || !draws) return;
    store.set(latest.current ? { id, input: latest } : null, owner);
  }, [store, draws, id, owner, key]);
  useEffect(
    () => () => {
      store?.set(null, owner);
    },
    [store, owner],
  );
  const [room, setRoom] = useState<number | null>(null);
  // frontend-development: not a fetch; hears the host's box size.
  useEffect(() => {
    if (!store || !on) return;
    // Under another id (an item opened in the box) the room stays what it
    // was until the host says it for the new one.
    const read = () => {
      const now = store.room(id);
      if (now !== null) setRoom(now);
    };
    read();
    return store.listen(read);
  }, [store, on, id]);
  return { drawn: on, room };
}
